import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

// How long the "added" confirmation stays on the button before it resets.
const ADDED_LABEL_DURATION = 2500;

/**
 * @typedef {{ size: string, id: number, price: number, compareAtPrice: number | null, available: boolean }} OfferVariant
 */

/**
 * Offer builder: one or more offers (`[data-offer-card]`), each with its own real Shopify
 * products/variants: the home page's radio offers, the shop page's duo banner.
 * Recomputes totals client-side from the variant data embedded by Liquid, and adds the
 * offer's lines to the cart through Horizon's canonical single-fetch + event flow.
 */
class OfferBuilderComponent extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    this.currency = this.dataset.currency || 'EUR';
    this.cartAddUrl = this.dataset.cartAddUrl;

    /** @type {HTMLElement[]} */
    this.cards = Array.from(this.querySelectorAll('[data-offer-card]'));

    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleClick);

    for (const card of this.cards) this.#updateCard(card);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    const card = target.closest('[data-offer-card]');
    if (card instanceof HTMLElement) this.#updateCard(card);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-role="add-to-cart"]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const card = button.closest('[data-offer-card]');
    if (card instanceof HTMLElement) this.#addToCart(card, button);
  };

  /** @param {HTMLElement} card */
  #getItems(card) {
    return Array.from(card.querySelectorAll('[data-role="offer-item"]'));
  }

  /** @param {Element} item */
  #getSelectedSize(item) {
    const radio = item.querySelector('.offer-card__size-radio:checked');
    return radio instanceof HTMLInputElement ? radio.value : null;
  }

  /**
   * Resolves the variant an item currently represents: the chosen size's real variant once
   * picked, otherwise the cheapest variant as a live preview (never an invented price).
   * @param {Element} item
   * @returns {OfferVariant | null}
   */
  #resolveVariant(item) {
    /** @type {OfferVariant[]} */
    let variants = [];
    try {
      variants = JSON.parse(/** @type {HTMLElement} */ (item).dataset.variants || '[]');
    } catch {
      variants = [];
    }
    if (variants.length === 0) return null;

    const hasSize = /** @type {HTMLElement} */ (item).dataset.hasSize === 'true';
    if (!hasSize) return variants[0] ?? null;

    const selectedSize = this.#getSelectedSize(item);
    if (!selectedSize) {
      return variants.reduce((cheapest, variant) => (cheapest === null || variant.price < cheapest.price ? variant : cheapest), /** @type {OfferVariant | null} */ (null));
    }
    return variants.find((variant) => variant.size === selectedSize) ?? null;
  }

  /**
   * Recomputes and repaints one card's total price and add-to-cart button state.
   * @param {HTMLElement} card
   */
  #updateCard(card) {
    const items = this.#getItems(card);
    let totalCents = 0;
    let compareCents = 0;
    let missingSize = false;
    let unavailable = false;

    for (const item of items) {
      const variant = this.#resolveVariant(item);
      if (variant) {
        totalCents += variant.price;
        compareCents += variant.compareAtPrice && variant.compareAtPrice > variant.price ? variant.compareAtPrice : variant.price;
      }
      if (/** @type {HTMLElement} */ (item).dataset.hasSize === 'true' && !this.#getSelectedSize(item)) missingSize = true;
      if (variant && variant.available === false) unavailable = true;
    }

    const formattedTotal = formatMoney(totalCents, this.moneyFormat, this.currency);

    const priceEl = card.querySelector('[data-role="offer-total"]');
    if (priceEl) priceEl.textContent = formattedTotal;

    const compareEl = card.querySelector('[data-role="offer-compare"]');
    if (compareEl) {
      if (compareCents > totalCents) {
        compareEl.textContent = formatMoney(compareCents, this.moneyFormat, this.currency);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.setAttribute('hidden', '');
      }
    }

    const button = card.querySelector('[data-role="add-to-cart"]');
    const buttonLabel = card.querySelector('[data-role="button-label"]');
    if (!(button instanceof HTMLButtonElement) || !buttonLabel) return;

    if (missingSize) {
      button.disabled = true;
      buttonLabel.textContent = button.dataset.chooseLabel ?? '';
    } else if (unavailable) {
      button.disabled = true;
      buttonLabel.textContent = button.dataset.soldOutLabel ?? '';
    } else {
      button.disabled = false;
      buttonLabel.textContent = `${button.dataset.addLabel ?? ''} · ${formattedTotal}`;
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLButtonElement} button
   */
  async #addToCart(card, button) {
    if (button.disabled) return;

    const items = this.#getItems(card);
    /** @type {{ id: number, quantity: number }[]} */
    const lines = [];
    for (const item of items) {
      const variant = this.#resolveVariant(item);
      if (!variant || !variant.available) {
        this.#announce(card, button.dataset.soldOutLabel ?? '');
        return;
      }
      lines.push({ id: variant.id, quantity: 1 });
    }
    if (lines.length === 0) return;

    const buttonLabel = card.querySelector('[data-role="button-label"]');
    const previousLabel = buttonLabel?.textContent ?? '';

    button.disabled = true;
    if (buttonLabel) buttonLabel.textContent = button.dataset.addingLabel ?? '';
    this.#announce(card, '');

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'offer-builder',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(
        this.cartAddUrl,
        fetchConfig('json', { body: JSON.stringify({ items: lines }) })
      );
      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || button.dataset.errorLabel || '';

        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );

        const ajaxCart = await this.#fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items },
        });

        this.#announce(card, message);
        button.disabled = false;
        if (buttonLabel) buttonLabel.textContent = previousLabel;
        return;
      }

      const ajaxCart = await this.#fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { didError: false, items: ajaxCart.items },
      });

      const addedLabel = button.dataset.addedLabel ?? '';
      if (buttonLabel) buttonLabel.textContent = addedLabel;
      this.#announce(card, addedLabel);

      window.setTimeout(() => {
        button.disabled = false;
        this.#updateCard(card);
      }, ADDED_LABEL_DURATION);
    } catch (error) {
      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : (button.dataset.errorLabel ?? ''),
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      this.#announce(card, button.dataset.errorLabel ?? '');
      button.disabled = false;
      if (buttonLabel) buttonLabel.textContent = previousLabel;
    }
  }

  async #fetchCart() {
    const cartUrl = /** @type {{ routes?: { cart_url?: string } }} */ (window.Theme)?.routes?.cart_url ?? '/cart';
    const response = await fetch(`${cartUrl}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * @param {HTMLElement} card
   * @param {string} message
   */
  #announce(card, message) {
    const liveRegion = card.querySelector('[data-role="live-region"]');
    if (liveRegion) liveRegion.textContent = message;
  }
}

if (!customElements.get('offer-builder-component')) {
  customElements.define('offer-builder-component', OfferBuilderComponent);
}
